import { useState, useEffect, useRef, useCallback, useLayoutEffect, Fragment } from "react";
import { useT } from "./i18n";
import { askLabReply, translateBabiUtterance, askLabExplainMore } from "./api";
import { useVoiceTurn } from "./useVoiceTurn";
import { renderChatContent } from "./AgentChat";
import { getCurriculum } from "./curriculum";
import { getTranslatedTopicName, stripSpokenMarkdown, formatVoiceMinutes, formatVoiceDuration } from "./utils";
import { getTargetLanguageBcp47, getLabTtsVoice } from "./tts";
import { getVoiceLimitSeconds, resolveVoiceBalance, VOICE_LOW_BALANCE_BUFFER_SECONDS } from "./constants";
import { getGrammarPoints } from "./grammarLab";

// Small timer/clock glyph for the voice-minutes card — Feather-style, matching the inline
// SVGs in FlashcardView. Colour comes from the .lab-minutes-icon class (gold accent).
const TimerIcon = () => (
  <svg className="lab-minutes-icon" width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="13" r="8" />
    <path d="M12 13V9" />
    <path d="M9 2h6" />
    <path d="m19 5-1.5 1.5" />
  </svg>
);

// The Lab's silence backstop — the VAD `redemptionMs` passed into useVoiceTurn. Deliberately
// longer than the chat widget's WIDGET_SILENCE_TIMEOUT_MS (1000ms): a practice utterance
// benefits from a slightly more patient cutoff than a quick clarifying question typed at the
// widget. Never conflated with the widget's constant — each surface owns its own.
export const LAB_SILENCE_TIMEOUT_MS = 3000;

// Speed control: a fixed two-option toggle (Normal / Slow), not a continuous slider — a
// beginner's actual need is "slow down enough to catch the words," not a range of speeds nobody
// asked for. 0.7 sits comfortably inside Google's documented 0.25-2.0 speakingRate range and
// close to the 0.6 value empirically confirmed live against tr-TR-Chirp3-HD-Achernar (the Lab's
// exact voice) during the speed-control investigation — a real, measurable duration change, not
// a silently-ignored parameter (that investigation also resolved a contradiction in Google's own
// docs about whether Chirp 3: HD voices honor this at all: they do). LAB_TTS_RATE_NORMAL is
// Google's own native-speed default (omitting the field entirely has the identical effect; kept
// as an explicit value here so the toggle's "which option is active" check has something to
// compare against).
export const LAB_TTS_RATE_NORMAL = 1.0;
export const LAB_TTS_RATE_SLOW = 0.7;

// Cap on learner speaking turns per Lab session (Babi's opening does not count). Sized around
// the voice-minutes budget more than tokens: each learner turn costs ~STT + a Babi TTS reply
// ≈ 30–40s billed, so 5 turns ≈ 3 minutes of a session — enough for a real mini-conversation
// without draining a trial user's 10-minute allowance in one sitting, and without feeling
// artificially clipped. Once the 5th learner turn is answered the session goes straight to the
// "ended" phase (Done / Practice another topic) — no separate "you hit the limit" screen.
export const LAB_MAX_LEARNER_TURNS = 5;

// Manual "Try again" attempts offered per turn when Babi's reply (or opening) fails in a way
// that's safe to retry. After this many, the session falls through to today's behaviour: end
// with the "Babi couldn't reply" message.
export const LAB_REPLY_RETRY_CAP = 2;

// Most separate mistakes Babi will mark (and highlight) in one reply, "3" — a longer learner turn
// can genuinely have this many, a short one usually has zero or one. Not a real constant here
// (nothing in this file reads it): the actual enforcement lives in api.js's own
// MAX_CORRECTIONS_PER_REPLY, and the prompt text below spells it out as "up to three" (small
// numbers read more naturally in English prose than as a digit) — if this ever changes, update
// api.js's constant and the prompt text to match.
// Brief pause before a retry actually fires — gives a transient upstream hiccup (429, 5xx,
// flaky connection) a moment to clear and avoids hammering an overloaded API.
const LAB_RETRY_DELAY_MS = 1000;

// Which reply/opening-generation failures are worth offering "Try again" for. The retry is
// safe in every case here: it never touches the learner's voice-minutes (those are billed in
// useVoiceTurn for STT/TTS only, never for the Claude call), token accounting counts only
// successful calls, and a lost response was never shown so there's no duplicate content.
//   - `err == null` → the call returned 200 but with an empty `reply` (non-deterministic;
//     a retry often succeeds).
//   - no `err.status` → a network-level throw (fetch rejected). Can't tell "died before the
//     model ran" from "died after" — accepted, matching speechToText's existing blind retry.
//   - 429 / 5xx → transient, unbilled, usually clears.
// NOT retryable: 400 (a malformed payload won't fix itself on an identical retry — that needs
// a code fix), 401/403 (auth won't resolve by retrying).
function isRetryableReplyFailure(err) {
  if (!err) return true;
  const s = err.status;
  if (s === 400 || s === 401 || s === 403) return false;
  return s == null || s === 429 || (s >= 500 && s <= 599);
}

// Single-session recap (Step 3.2 follow-up) — every number/grouping the recap card shows,
// derived purely from turnMeta/messages exactly as they sit in state when "ended" renders. No
// side effects, no i18n (the component wraps this in translated JSX) — exported so its index
// arithmetic (messages[idx - 1], the same class of bug this file's turnMeta refactor exists to
// prevent) gets real unit coverage, not just a live/manual check. Returns null when there's no
// graded turn to recap (an opening-only session, or one that ended via a failure before any
// reply was graded) — same "nothing to credit" gate creditSessionIfEarned already uses.
export function computeSessionRecap(turnMeta, messages) {
  const gradedTurns = Object.values(turnMeta);
  if (!gradedTurns.length) return null;

  // Every turnMeta entry has a valid 1-3 stars by construction (askLabReply's own validation +
  // the unconditional write in generateReply) — trusted here, not re-checked.
  const avgStars = gradedTurns.reduce((sum, m) => sum + m.stars, 0) / gradedTurns.length;
  // "Error-free" requires BOTH grading signals to agree there was no mistake, not just errorTag.
  // errorTag/stars and corrections are two independently-produced judgments from the same model
  // call — errorTag confirmed live to sometimes come back null on a turn that DID get a real
  // [[ ]]/{{ }} correction (the model filled in the reply's inline markers correctly but dropped
  // the separate errorTag field), which silently counted a turn with a visible correction bubble
  // as "error-free". corrections.length is the guaranteed-by-construction signal (carved directly
  // out of reply, same as extractCorrectionMarkers) — trust it over errorTag alone.
  const errorFreeCount = gradedTurns.filter(m => m.errorTag == null && (m.corrections?.length ?? 0) === 0).length;

  // Free-text errorTag, grouped as-is (no normalisation this pass — flagged in the build report
  // as the one field that would benefit from §7's controlled taxonomy).
  const mistakesByType = gradedTurns.reduce((acc, m) => {
    if (m.errorTag) acc[m.errorTag] = (acc[m.errorTag] || 0) + 1;
    return acc;
  }, {});

  // Every correction across every graded turn, paired with the learner's own line that prompted
  // it (messages[idx - 1] — the user turn immediately before that graded reply), in message
  // order (Object.entries on integer-like keys enumerates ascending, per spec).
  const correctionRecap = Object.entries(turnMeta).flatMap(([idxStr, meta]) => {
    const idx = Number(idxStr);
    const learnerLine = messages[idx - 1]?.content || "";
    return (meta.corrections || []).map((c, i) => ({ recapKey: `${idx}-${i}`, learnerLine, ...c }));
  });

  return { gradedCount: gradedTurns.length, avgStars, errorFreeCount, mistakesByType, correctionRecap };
}

// §7 progress tracking: tallies grammarPointId across every graded turn — same source
// (turnMeta) and same shape of aggregation as computeSessionRecap's mistakesByType above, just
// keyed by grammarPointId instead of errorTag. Deliberately kept separate from
// computeSessionRecap itself (which stays untouched — it drives the recap card, unrelated to
// this) rather than folded into its return value. Turns with a null grammarPointId (no mistake,
// or a spelling/content-only mistake — see askLabReply's validation) contribute nothing, exactly
// like a null errorTag contributes nothing to mistakesByType. Called with turnMetaRef.current at
// session-complete time, never the `turnMeta` state variable directly — see turnMetaRef's comment.
export function computeGrammarTally(turnMeta) {
  return Object.values(turnMeta).reduce((acc, m) => {
    if (m.grammarPointId) acc[m.grammarPointId] = (acc[m.grammarPointId] || 0) + 1;
    return acc;
  }, {});
}

// Low-balance warning (plan doc §8 follow-up): reuses the SAME VOICE_LOW_BALANCE_BUFFER_SECONDS
// threshold AgentChat.js already checks for the widget's model-driven wrap_up_conversation note —
// one consistent definition of "running low" across the app — but the Lab has no BABI_TOOLS/
// model-driven sign-off to pair it with, so this drives a plain persistent banner instead,
// evaluated purely at this consumer level (mirrors AgentChat.js's own pattern) with zero changes
// to useVoiceTurn.js/handleMicClick. Pulled into its own pure function, same reason
// computeSessionRecap/computeGrammarTally are: real unit coverage instead of only a live check.
// The explicit `> 0` guard (not just `voiceStage !== "no-balance"`) matters at the exact instant
// balance hits zero: voiceStage may not have transitioned to "no-balance" yet on this same render,
// and "almost out" would be a misleading message once truly out — 0 remaining is always the hard-
// stop banner's territory exclusively, never this softer one's, regardless of voiceStage timing.
export function shouldShowLowBalanceWarning(voiceRemainingSeconds, voiceStage) {
  return voiceRemainingSeconds > 0
    && voiceRemainingSeconds <= VOICE_LOW_BALANCE_BUFFER_SECONDS
    && voiceStage !== "no-balance";
}

// Synthetic opening user message — the Anthropic API requires the first message to be `user`.
// The system prompt is what actually tells Babi to open; this content barely matters. It stays
// as messages[0] for the whole session and is never rendered.
const LAB_KICKOFF = "Let's begin the practice conversation.";

// §7: the full Grammar Lab taxonomy, offered to the grading model as its only selectable
// grammarPointId options — built once at module load (getGrammarPoints("turkish") is static
// content today), not per-render. Letting the model pick from the actual list (using the
// specific wrong word/suffix it already identified) rather than inventing a category name is
// what makes grammarPointId reliable enough to feed labGrammarTally directly, unlike free-text
// errorTag.
const GRAMMAR_POINT_OPTIONS = getGrammarPoints("turkish").map((gp) => `${gp.grammar_point_id}: ${gp.title}`).join("; ");

// Minimal, topic-scoped system prompt. Deliberately NOT AgentChat's full Babi persona — no
// product-support role, no BABI_TOOLS, no escalation, no wrap-up, no satisfaction check. Two
// modes: "opening" (Babi speaks first) and "reply" (Babi answers the learner). The spoken
// `reply` text carries no-markdown / keep-it-brief instructions since TTS reads it aloud.
// `targetLanguage` is the course language (profile.targetLanguage, e.g. "Turkish") — the Lab
// operates ONLY in it, never the learner's base/UI language.
//
// The model answers with a small JSON envelope ({"reply": "..."}) rather than bare text so a
// later step can add feedback / errorTag / stars fields to the same call — see askLabReply.
function buildLabSystemPrompt(topic, targetLanguage, mode) {
  const vocab = (topic?.words || []).map(w => w.turkish).filter(Boolean).join(", ");
  const base = `You are Babi, a warm, encouraging ${targetLanguage} conversation partner helping a learner practise speaking.
Topic: "${topic?.topic || "this topic"}" — the learner has just finished studying it.
Reply ONLY in ${targetLanguage}. Every sentence must be ${targetLanguage} — never switch to, translate into, or mix in any other language, not even a single word of encouragement.
Stay within the vocabulary and grammar this topic covers. Its ${targetLanguage} words are: ${vocab || "(the basics)"}. Keep the ${targetLanguage} simple and beginner-appropriate.
Never mention the app, tools, minutes, or that you are an assistant.
The learner is speaking; their words reach you as a text transcript. If numbers in that transcript appear as digits ("1 2 3", "25"), the learner said those numbers aloud in ${targetLanguage} — read them as the ${targetLanguage} number words they spoke and treat them as correctly said. Never tell the learner to say a number "in ${targetLanguage}" or treat a digit as a wrong-language mistake. The same applies to times of day: if a time appears in the transcript as digits/numerals (e.g. "5.30", "17:45"), the learner spoke that time naturally in ${targetLanguage} words (e.g. "saat beş buçuk", "saat on yedi kırk beş") — a numeral transcript is ambiguous about which exact words were used, so treat the time itself as correctly said and never claim a specific word (like "buçuk", "çeyrek", "geçiyor", "var") was missing just because the transcript shows digits instead of that word. And in your OWN reply, never write a number or a time as digits/numerals — always spell it out in natural spoken ${targetLanguage} words (e.g. write "saat beş buçuk", not "saat 5.30" or "17:45"), since your reply is read aloud and numerals wouldn't sound like real spoken ${targetLanguage}.`;

  // Correction discipline — Babi must catch EVERY real error in the learner's sentence, not
  // only the obvious ones. Small suffix/spelling slips were being silently let through.
  const correcting = `Before replying, check the learner's last sentence carefully for ANY ${targetLanguage} mistake — the small ones as well as the obvious ones. For Turkish that includes: vowel harmony; consonant assimilation on suffixes (the ablative hardens to "-ten/-tan" after a voiceless consonant — f, s, t, k, ç, ş, h, p — so since "on beş" ends in "ş" it is "15'ten sonra", not "15'den sonra"; the same hardening applies to "-te/-ta", "-tir/-tır", "-çi", etc.); picking the right case suffix; buffer consonants (-y-, -n-, -s-); word order; tense; and spelling — including the Turkish-specific letters ç, ş, ğ, ı, İ, ö, ü: a transcript that drops one of these or swaps it for its plain-ASCII look-alike (e.g. "cok" for "çok", "bugun" for "bugün", "guzel" for "güzel") is a real spelling mistake too — be careful here, and correct it the same as any other, don't let it pass just because the rest of the word is recognisable. If the sentence has one or more mistakes — even a tiny one — give each one its own corrected version, marked as described below, each in its own short sentence (e.g. Doğrusu [[...]] olmalı.). A short answer usually has just one mistake; a longer answer can genuinely have several — mark up to three separate mistakes this way, each with its own [[ ]] (and, when you're sure which word or suffix changed, its own {{ }} inside it). Only mark as many as are REALLY there: most turns will have zero or one, never split a single mistake into more than one marker, and never invent extra ones just because you're allowed up to three. If there are more than three, just fix the three most important ones rather than overwhelming the learner. Do NOT explain the rule, do NOT list the errors, do NOT lecture — just the marked corrected phrase(s), nothing else about them. Say nothing about a correction (no markers at all) when the sentence is genuinely error-free. Never flag a valid alternative or a stylistic choice as a mistake.`;

  // Content/meaning check — separate from grammar. Confirmed live (2026-09-11) that a
  // grammatically clean sentence can still fail to be a real answer: (1) the learner answers the
  // wrong thing (asked which day follows Sunday, answered "Thursday" instead of "Monday") and
  // Babi didn't notice; (2) the learner doesn't answer at all, just echoes/rephrases Babi's own
  // question back, and Babi corrected its grammar then treated the echo AS an answer — even
  // complimenting it as "a nice question." Grammar-correctness alone can't catch either case.
  const contentCheck = `Separately from grammar, also judge whether what the learner just said is actually a sensible, real answer to what YOU (Babi) just asked or said — a grammatically correct sentence can still fail this. Watch for two patterns:
- The answer is grammatically fine but factually or logically wrong for your question (e.g. you asked what day comes right after Sunday and they said "Thursday" instead of "Monday"). Gently give the correct answer in one short, warm sentence — same non-lecturing tone as a grammar fix.
- The learner didn't actually answer at all — most often by repeating or rephrasing YOUR question back to you (sometimes with a small grammar mistake of their own) instead of answering it. Recognise this for what it is: never compliment it as if it were a new question from them, and never treat it as their answer.
If their answer already makes real sense, don't comment on this at all — just continue naturally. Never use the [[ ]] / {{ }} markers for a content issue — those are for grammar corrections only.`;

  // The correction is marked INLINE inside "reply" (see extractCorrectionMarkers in api.js)
  // rather than repeated in separate correction/correctionFocus JSON fields — a single string
  // can't disagree with itself, which is what made the old two-field version silently drop the
  // highlight whenever Babi's two copies of the same phrase didn't match character-for-character.
  const format = `Respond with ONLY a JSON object and nothing else — no markdown, no code fences, no text before or after it:
{"reply": "<your spoken ${targetLanguage} reply>"}
The "reply" text is read aloud by text-to-speech, so it must be plain spoken sentences: no markdown, no asterisks, no bullet points, no headings. Keep it short — 2 to 5 sentences total, and at most three of them for a correction.
When (and only when) you correct a mistake per the rule above, mark it directly inside "reply", right where you naturally say it: wrap the whole corrected phrase in double square brackets [[ ]], and — nested inside those — wrap just the specific word or suffix you changed in double curly braces {{ }}. ALSO put real quotation marks directly around that same corrected phrase, just OUTSIDE the [[ ]] (not inside it, and not around any other part of your sentence). The [[ ]]/{{ }} marks themselves are stripped out before the learner ever sees or hears your reply — the quotation marks are NOT stripped, so they're what's left marking the exact phrase for the learner, and they also let a later translation of your words preserve that exact phrase instead of having to guess where it starts and ends. The {{ }} must land exactly on the word(s) that were actually wrong in the learner's own sentence — it can be the first word of the phrase, the last, or anywhere in between; never default to marking the last word of the phrase just because it comes last, and never let a correct trailing word get caught inside {{ }}. Example (mistake near the end): learner said "hem çay hem da kahve" — you would write ...Doğrusu "[[hem çay hem {{de}} kahve]]" olmalı... . Example (mistake in the middle, correct words after it): learner said "benim doğum tarihi da yedi var" — you would write ...Doğrusu "[[doğum {{tarihimde de}} yedi var]]" olmalı... (note "yedi var" was already correct and stays OUTSIDE {{ }}, even though it's the last part of the phrase — and the closing quote still goes after it, at the end of the whole [[ ]] span). If the learner's sentence has more than one separate mistake, use a SEPARATE [[ ]] (each with its own {{ }} and its own pair of quotation marks) for each one, up to three per reply — never combine two unrelated fixes inside one [[ ]] pair. If you're not sure exactly which word or suffix changed within a phrase, still wrap that whole corrected phrase in [[ ]] and quotation marks and simply leave out its {{ }} — never guess or wrap the wrong part. Use [[ ]] / {{ }} / quotation marks ONLY for actual grammar corrections, up to three separate ones per reply, and nowhere else.`;

  // Step 3.2 (grading): three more fields in the SAME JSON object, appended rather than folded
  // into `format` above so the already-tuned reply/marker instructions stay untouched. Only for
  // "reply"/"closing" — the learner's opening has no utterance yet to grade, so `format` alone
  // is used there. errorTag/grammarPointId/feedback are internal (never shown to the learner
  // directly, product decision) — kept in English regardless of ${targetLanguage}/the learner's
  // base language so they read consistently for whoever/whatever consumes them later (the
  // on-demand "Explain more" call, and §7's grammar-point tally).
  // §7: grammarPointId sits ALONGSIDE errorTag, not instead of it — errorTag stays exactly as it
  // was (free-text category, feeds the recap card's "mistakes by type" display unchanged).
  // grammarPointId is separately constrained to a real Grammar Lab point id (or null) so it can
  // feed labGrammarTally directly without a fuzzy-matching layer — the model picks from the same
  // fine-grained correction it already produced (the actual wrong word/suffix), not a guess from
  // a category name.
  const grading = `Also add three more fields to that SAME JSON object, all about the learner's last sentence specifically (not your own reply):
{"stars": <1, 2, or 3>, "errorTag": <string or null>, "grammarPointId": <string or null>, "feedback": <string or null>}
"stars" — always include it. 3 = the sentence was completely correct: no grammar mistake AND it made real sense as an answer (per the rules above). 2 = right, but for one small/minor slip. 1 = multiple mistakes, or one significant one.
"errorTag" and "feedback" — both null when stars is 3. Otherwise, in English (not ${targetLanguage}, not the learner's own language — these two fields are internal and never shown to the learner directly): "errorTag" is a short lowercase category for the single most significant mistake (e.g. "vowel harmony", "word order", "case suffix", "consonant assimilation", "spelling/diacritics", or "content" for a wrong-answer/non-answer per the content rule above); "feedback" is one short plain-English sentence naming what that mistake was.
"grammarPointId" — null when stars is 3, and also null when the single most significant mistake is NOT a grammar-point issue (a spelling/diacritics slip, or a content/wrong-answer mistake per the rules above — neither of those maps to a grammar point). Otherwise, the id of whichever ONE grammar point from this exact list best matches that mistake — use the specific wrong word/suffix you already identified to pick the closest real match, not a guess from the category name alone. Never invent an id and never return one that isn't in this list — if genuinely nothing fits, use null. The list (id: title): ${GRAMMAR_POINT_OPTIONS}`;

  if (mode === "opening") {
    return `${base}
You speak first. Open the conversation with a short, friendly ${targetLanguage} greeting and one simple question about the topic, to get the learner talking. Do not wait for them.
${format}`;
  }
  if (mode === "closing") {
    return `${base}
${correcting}
${contentCheck}
This is the FINAL exchange — the conversation ends right after your reply. Respond warmly to what the learner just said with a brief compliment, apply the grammar and content rules above, then a short friendly closing line. Do NOT ask a question and do NOT invite them to say more — there will be no further turn.
${format}
${grading}`;
  }
  return `${base}
${correcting}
${contentCheck}
Respond naturally to what the learner just said (after handling any grammar or content issue from the rules above), then warmly invite them to say a little more — if they didn't really answer your last question, simply ask it again instead of moving to something new.
${format}
${grading}`;
}

// Elite-only gate for an active Pro (Basic-tier) subscriber — Elite is a tier CHANGE in
// Settings, not a fresh checkout, so the generic Pro/Elite selector paywall is a dead end for
// them. This points them at the right place instead.
export function LabEliteUpsell({ onGoToSettings, onBack }) {
  const { t } = useT();
  return (
    <div className="paywall-screen">
      <button className="back-btn" onClick={onBack}>{t("lab.back")}</button>
      <div className="paywall-card">
        <div className="paywall-lock">🎧</div>
        <h2 className="paywall-title">{t("lab.eliteUpsellTitle")}</h2>
        <p className="paywall-sub">{t("lab.eliteUpsellBody")}</p>
        <button className="paywall-cta" onClick={onGoToSettings}>{t("paywall.pausedGoToSettingsBtn")}</button>
      </div>
    </div>
  );
}

// Slice 1 + Step 3.1: pick one already-completed topic; Babi opens the conversation (text +
// spoken); the learner and Babi then trade several spoken turns (up to LAB_MAX_LEARNER_TURNS),
// each learner turn confirmed explicitly with no auto-send; then the session ends. No grading,
// no feedback, no stars, no progress tracking yet — those come in Step 3.2.
export function ListenSpeakLab({ profile, subscription, isAdminAccount, isSimpleViewer, completedTopics, onUpdateProfile, onSessionComplete, onBack, initialTopicId, onInitialTopicConsumed }) {
  const { t, lang, isRTL } = useT();
  const targetLanguage = profile?.targetLanguage || "Turkish";
  const langCode = targetLanguage.toLowerCase();
  const baseLanguage = profile?.baseLanguage || "English";
  const canTranslate = baseLanguage !== targetLanguage;

  // initialTopicId is a one-shot deep-link (e.g. the Lesson page's "Practice in Listen & Speak
  // Lab" button) — only consulted in these lazy initializers, which run once at mount, same
  // pattern as GrammarLabView's initialPointId. Only honored if the topic is actually completed
  // (the Lab's own long-standing rule — "pick one already-completed topic"); an invalid/not-yet-
  // completed id is silently ignored and the screen falls back to its normal "pick" landing,
  // rather than deep-linking into a topic the learner can't actually practice yet.
  const initialTopic = (initialTopicId && completedTopics?.[initialTopicId])
    ? getCurriculum(langCode).find(tp => tp.topic_id === initialTopicId) || null
    : null;

  // "pick" → choose a finished topic; "intro" → topic card + Start Conversation button;
  // "talk" → the running conversation (Babi opens, then turns trade); "ended" → transcript +
  // Done / Practice another topic.
  const [phase, setPhase] = useState(initialTopic ? "intro" : "pick");
  const [topic, setTopic] = useState(initialTopic);
  useEffect(() => {
    if (initialTopicId) onInitialTopicConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Speed control: pure component state, deliberately NOT persisted to profile (never read from
  // or written to it) — resets to LAB_TTS_RATE_NORMAL at the start of every new session
  // (pickTopic below), same "fresh each time" treatment as topic/messages/turnMeta. The LIVE
  // reply (the next speakReply() call) is synthesized at whatever value is active when that call
  // starts — never retroactive for audio already mid-playback. But "Listen again" is NOT
  // generation-time-locked: playTurnAudio re-derives the correct playbackRate from the CURRENT
  // value of this state against whatever rate the cached clip was generated at (turnAudio's
  // `.rate`), every time it's tapped — no new API call, so flipping the toggle and re-listening to
  // ANY past turn (not just the most recent one) always matches the toggle's current setting.
  const [ttsRate, setTtsRate] = useState(LAB_TTS_RATE_NORMAL);
  // The running conversation in Anthropic message shape. messages[0] is the synthetic
  // LAB_KICKOFF `user` turn (never rendered); after that, alternating assistant/user turns.
  // This array IS the history handed to every askLabReply call, so Babi stays coherent across
  // turns — the same structure Slice 1 built inline for its single turn, just accumulated.
  const [messages, setMessages] = useState([]);
  // `messages` / `messagesRef` hold ONLY { role, content } — that array is replayed verbatim to
  // the Anthropic API and it 400s on any other key (this is exactly the bug fixed in 09cfe92).
  // Per-message render metadata lives here instead, keyed by the message's index in `messages`:
  //   { [index]: { corrections: [{ correction, correctionFocus }, ...], stars, errorTag, grammarPointId, feedback } }
  //   (up to LAB_MAX_CORRECTIONS_PER_REPLY correction entries, in the order Babi said them.
  //   Step 3.2: stars/errorTag/feedback are set unconditionally on every graded reply/closing
  //   turn now, not just ones with a correction — see generateReply. Nothing reads turnMeta[idx]
  //   as a plain truthy/existence check anywhere (only `?.corrections`/`?.stars`/`?.errorTag` on
  //   specific fields), so a turn that previously had no entry at all now always having one is
  //   safe — traced deliberately before making this change unconditional. §7: grammarPointId
  //   joins the same unconditional set, same reliability standard as stars/errorTag.)
  const [turnMeta, setTurnMeta] = useState({});
  // Mirrors `turnMeta`, same reason `messagesRef` mirrors `messages`: the final graded turn's
  // entry is written via setTurnMeta and endSession() can fire in that SAME synchronous tick
  // (see generateReply's `if (isFinalTurn) endSession()`) — a plain read of the `turnMeta` state
  // variable there would be one render stale, missing exactly the last turn's grammarPointId.
  // computeGrammarTally always reads turnMetaRef.current, never the state variable, for this
  // reason. Kept in sync by syncTurnMeta below — never call setTurnMeta directly.
  const turnMetaRef = useRef({});
  const syncTurnMeta = (updater) => {
    setTurnMeta((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      turnMetaRef.current = next;
      return next;
    });
  };
  // Terminal "the session ended on a failure" flag — drives the error banner on the ended screen.
  const [replyFailed, setReplyFailed] = useState(false);
  // A still-retryable reply/opening failure: keeps the session in "talk" with a "Try again"
  // button instead of ending. { mode: "reply" | "opening", attempt: number }.
  const [replyError, setReplyError] = useState(null);
  // "Explain more" (Step 3.2, §6 point 6) — the on-demand third call's result, per graded turn,
  // keyed by that turn's index in `messages`: { [index]: { status: "loading" | "done" | "error", text? } }.
  // Same shape/pattern as `translations` just below (a secondary, on-demand per-turn lookup, not
  // part of the primary reply-generation flow) — deliberately NOT folded into turnMeta, which
  // holds only what the combined grading+reply call itself returned.
  const [explainMore, setExplainMore] = useState({});
  // "Translate what Babi said" — per assistant turn, keyed by that turn's index in `messages`:
  //   { [index]: { status: "loading" | "done" | "error", text? } }
  const [translations, setTranslations] = useState({});
  // "Listen again" — `speakReply`'s return value ({ audioContent, rate }) for every spoken Babi
  // turn this session, keyed by that turn's index in `messages`. `rate` is the speakingRate that
  // clip was actually generated at (whatever the toggle said at the time) — kept alongside the
  // audio so a later replay can compare it against the toggle's CURRENT value and correct the
  // playback speed accordingly (see playTurnAudio) even if the toggle has since changed. Session-
  // scoped only (plain state, never persisted): the whole Lab session already caps at one opening
  // + LAB_MAX_LEARNER_TURNS replies, so this holds at most 6 short clips — no eviction logic
  // needed, it's cleared automatically when the Lab unmounts. A missing entry (TTS failed/
  // unavailable for that turn, or ttsEnabled is off) means there's simply nothing to replay —
  // renderListenAgain returns null for it. Replaying from this cached clip is a local `Audio`
  // with no billing listener attached, so — unlike the original synthesis+playback in speakReply
  // — it never re-bills voice minutes (product decision, confirmed: a "Listen again" tap uses no
  // API and costs nothing extra).
  const [turnAudio, setTurnAudio] = useState({});
  // Index of the turn whose cached audio is currently replaying (or null) — drives which "Listen
  // again" button shows as active, and lets a new tap stop whatever's already playing so at most
  // one clip plays at a time.
  const [replayingIndex, setReplayingIndex] = useState(null);
  // The actual replay `Audio` instance in flight, so a new tap (or a tap on the same button again)
  // can .pause() the previous one before starting another — separate from useVoiceTurn's own
  // currentAudioRef, which only tracks the LIVE reply's audio, not a cached replay.
  const replayAudioRef = useRef(null);

  const pendingTextareaRef = useRef(null);
  // Bumped on every pickTopic / restart — a reply or translate call that resolves after the
  // session changed is dropped rather than landing on a fresh screen.
  const sessionRef = useRef(0);
  // messagesRef mirrors `messages` so the hook's deferred onSubmitTranscript callback (which
  // reaches runReply long after the render that scheduled it) always reads the CURRENT history,
  // never a stale closure — same reason Slice 1 kept openingRef. syncMessages keeps both in
  // step; runReply/startConversation always read messagesRef.current, not `messages`.
  const messagesRef = useRef([]);
  // useVoiceTurn's deferred callback (the confirmed-transcript submit) fires long after this
  // render; route it through a ref so it always reaches the current runReply, which closes over
  // hook returns (setVoiceStage/speakReply) and so can't be defined before the hook.
  const runReplyRef = useRef(null);
  // Streak credit (Step 3.2 wiring): `sessionStartedAtRef` is stamped when Babi actually opens
  // (not when the topic is picked — the learner may sit on the intro screen). `sessionCreditedRef`
  // makes onSessionComplete fire at most once per session. Both reset in pickTopic / restart.
  const sessionStartedAtRef = useRef(null);
  const sessionCreditedRef = useRef(false);
  const handleTranscript = useCallback((text, promptBuilder) => runReplyRef.current?.(text, promptBuilder), []);
  const buildPrompt = useCallback(
    (isFinalTurn) => buildLabSystemPrompt(topic, targetLanguage, isFinalTurn ? "closing" : "reply"),
    [topic, targetLanguage]
  );

  const {
    voiceStage, setVoiceStage,
    transcriptState, setTranscriptState,
    pendingVoiceText, setPendingVoiceText,
    ttsNotice, setTtsNotice,
    micSupported, requestMic, handleMicClick, voiceRemainingSeconds,
    beginEditingPendingVoiceText, confirmPendingVoiceText, redoPendingVoiceText,
    speakReply, armTurn, cancelTurn, isTurnActive,
  } = useVoiceTurn({
    profile, subscription, isAdminAccount, isSimpleViewer, onUpdateProfile,
    // The Lab screen only mounts when the learner is on it and about to talk, so treat it like
    // an open panel: preload the VAD's WASM/model in the background on mount so the first mic
    // tap isn't a multi-second cold compile (which also shifts "Listening…" earlier and lets
    // the pre-speech buffer start filling sooner — see the first-word-clipping fix).
    panelOpen: true,
    enablePreload: true,
    silenceTimeoutMs: LAB_SILENCE_TIMEOUT_MS,
    autoSendPendingReview: false,        // the Lab NEVER auto-sends — every learner turn is confirmed
    ttsEnabled: true,                   // the Lab DOES want Babi's spoken reply (unlike the widget)
    languageCode: getTargetLanguageBcp47(profile),  // STT + TTS in the TARGET language, not the base
    ttsVoice: getLabTtsVoice(profile),  // the Lab's own warm female Chirp3-HD voice, not the app's male Wavenet-E
    ttsRate,                            // speed-control toggle's current value (Normal/Slow) — the widget never passes this
    buildSystemPrompt: buildPrompt,
    onSubmitTranscript: handleTranscript,
  });

  // Only fully-completed lessons (the completedTopics ledger), in curriculum order — not the
  // looser "has a flashcard" notion used by My Vocabulary.
  const completedList = getCurriculum(langCode)
    .filter(tp => completedTopics?.[tp.topic_id])
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // At-a-glance voice-minutes balance on the topic-list page, so a learner isn't bouncing to
  // Settings to check what's left. Shown exactly the way Settings shows it: the monthly
  // allowance on the main line (remaining, to the second), and the persistent purchased pool
  // — if any — on its own line below, reusing the same string. Shared formatting helpers.
  const voiceLimitSeconds = getVoiceLimitSeconds(profile, subscription, isAdminAccount, isSimpleViewer);
  const voiceBal = resolveVoiceBalance(profile, subscription);
  const voiceMonthlyRemaining = Math.max(0, voiceLimitSeconds - voiceBal.usedSeconds);
  const voicePurchasedSeconds = voiceBal.purchasedSeconds;

  // Learner turns taken so far (LAB_KICKOFF is a `user` message but not a real turn, hence −1).
  const learnerTurnCount = Math.max(0, messages.filter(m => m.role === "user").length - 1);
  const lastMessage = messages[messages.length - 1];
  const atTurnCap = learnerTurnCount >= LAB_MAX_LEARNER_TURNS;
  // Babi has just spoken and it's the learner's move (mic / "Continue conversation" affordance).
  const learnerCanRespond = lastMessage?.role === "assistant" && !atTurnCap;

  const syncMessages = next => { messagesRef.current = next; setMessages(next); };
  const appendMessage = entry => syncMessages([...messagesRef.current, entry]);

  // Credit the session toward the learner's daily streak (Interpretation B): fire once, only if
  // at least one full learner exchange happened (learnerTurns >= 1). An opening-failure with no
  // exchange, or an instant Back, credits nothing. App.js's handleLabSessionComplete does the
  // actual streak/dailyLog write — this just hands it the turn count, elapsed minutes, and (§7)
  // the session's grammar-point tally. Reads turnMetaRef.current, NOT the `turnMeta` state
  // variable — on the turn-cap auto-end path (generateReply's `if (isFinalTurn) endSession()`)
  // this runs in the same synchronous tick as the final turn's syncTurnMeta call, so the state
  // variable itself would still be one render behind.
  const creditSessionIfEarned = () => {
    if (sessionCreditedRef.current) return;
    const learnerTurns = Math.max(0, messagesRef.current.filter(m => m.role === "user").length - 1);
    if (learnerTurns < 1) return;
    sessionCreditedRef.current = true;
    const startedAt = sessionStartedAtRef.current;
    const minutes = startedAt ? Math.round((Date.now() - startedAt) / 60000) : 0;
    const grammarTally = computeGrammarTally(turnMetaRef.current);
    onSessionComplete?.({ learnerTurns, minutes, grammarTally });
  };

  // The single path to the "ended" phase — the turn cap, a mid-conversation reply failure, an
  // opening failure, and the "Finish" button all go through here so streak credit is evaluated
  // exactly once, consistently.
  const endSession = () => { creditSessionIfEarned(); setPhase("ended"); };

  // Given a generation failure (or an empty reply), either surface a "Try again" affordance
  // (retryable + attempts left) or fall through to today's behaviour: end the session with the
  // "Babi couldn't reply" message. `mode` picks which generator the retry button re-runs.
  const handleGenerationFailure = (mode, err, attempt) => {
    if (isRetryableReplyFailure(err) && attempt < LAB_REPLY_RETRY_CAP) {
      setReplyError({ mode, attempt });
      setVoiceStage("idle");
    } else {
      setReplyFailed(true);
      endSession();
    }
  };

  // Generate Babi's opening. Idempotent — always resets the history to [kickoff] first — so the
  // "Try again" button can re-run it without piling up messages.
  const generateOpening = async ({ attempt = 0 } = {}) => {
    const s = sessionRef.current;   // snapshot — a Back+repick mid-await invalidates this run
    setReplyFailed(false);
    setReplyError(null);
    setVoiceStage("replying");
    syncMessages([{ role: "user", content: LAB_KICKOFF }]);
    let result = null, err = null;
    try { result = await askLabReply(messagesRef.current, buildLabSystemPrompt(topic, targetLanguage, "opening"), 700); }
    catch (e) { err = e; }
    // Back / restart (isTurnActive) OR a whole new session started mid-generation (sessionRef
    // moved) — either way this run is stale, drop it.
    if (s !== sessionRef.current || !isTurnActive()) return;
    // stripSpokenMarkdown: safety net so a stray *asterisk* / "- bullet" from Claude isn't
    // read out literally by TTS — same cleaned string for the bubble and the spoken audio.
    const clean = stripSpokenMarkdown(result?.reply || "");
    if (!clean) {
      // Opening failed before any exchange — endSession credits nothing (learnerTurns is 0).
      handleGenerationFailure("opening", err, attempt);
      return;
    }
    appendMessage({ role: "assistant", content: clean });
    const audioIdx = messagesRef.current.length - 1;
    const spoken = await speakReply(clean);
    if (s !== sessionRef.current) return;   // speakReply can take seconds — recheck before the reset
    if (spoken) setTurnAudio(p => ({ ...p, [audioIdx]: spoken }));
    setVoiceStage("idle");
  };

  // Show the learner's confirmed turn immediately, BEFORE the model call — so a retry of the
  // generation step never re-appends it (two `user` messages in a row → Anthropic 400).
  const appendUserTurn = text => { appendMessage({ role: "user", content: text }); };

  // Generate Babi's reply for the turn already sitting in messagesRef. Called once by runReply
  // and again by the "Try again" retry; it only ever appends Babi's reply (on success), so it's
  // safe to run repeatedly for one turn. Step 3.2: result.stars/errorTag/feedback (the SAME call,
  // already validated by askLabReply) are stored into turnMeta below — no second AI call for
  // grading; a retry naturally re-grades too, same as it re-generates the reply.
  const generateReply = async ({ attempt = 0 } = {}) => {
    const s = sessionRef.current;   // snapshot — a Back+repick mid-await invalidates this run
    setReplyFailed(false);
    setReplyError(null);
    setVoiceStage("replying");
    stopReplayAudio(); // a fresh live turn is about to speak — a lingering replay must not overlap it
    // −1 for the synthetic LAB_KICKOFF at messages[0]. When this is the learner's last allowed
    // turn, Babi gets the "closing" prompt so she wraps up warmly instead of asking a question
    // nobody can answer — the session ends the moment her reply lands.
    const learnerTurns = messagesRef.current.filter(m => m.role === "user").length - 1;
    const isFinalTurn = learnerTurns >= LAB_MAX_LEARNER_TURNS;
    const system = buildPrompt(isFinalTurn);
    let result = null, err = null;
    try { result = await askLabReply(messagesRef.current, system, 1000); }
    catch (e) { err = e; }
    if (s !== sessionRef.current || !isTurnActive()) return;
    const clean = stripSpokenMarkdown(result?.reply || "");   // see generateOpening's note
    if (!clean) {
      // Reply failed mid-conversation — endSession still credits the session if the learner
      // already completed at least one exchange (creditSessionIfEarned checks learnerTurns >= 1).
      handleGenerationFailure("reply", err, attempt);
      return;
    }
    appendMessage({ role: "assistant", content: clean });
    // Up to LAB_MAX_CORRECTIONS_PER_REPLY corrected phrases (and the fixed word within each) live
    // in turnMeta keyed by the message's index, NOT on the message object — see the turnMeta
    // declaration. Each is kept only when it's an exact substring of what it's split against,
    // since the renderer splits verbatim; a near-miss just drops that one highlight, not a bug.
    // Since askLabReply now derives correction/correctionFocus by carving them OUT of reply (see
    // extractCorrectionMarkers, api.js) rather than matching a separately-generated copy, this
    // `.includes()` check should always pass in practice — it's kept as a cheap belt-and-braces
    // guard per entry, not the primary mechanism it used to be.
    const corrections = (result?.corrections || [])
      .map(({ correction, correctionFocus }) => {
        const rawCorrection = correction ? stripSpokenMarkdown(correction) : "";
        if (!rawCorrection || !clean.includes(rawCorrection)) return null;
        const rawFocus = correctionFocus ? stripSpokenMarkdown(correctionFocus) : "";
        const validFocus = rawFocus && rawCorrection.includes(rawFocus) ? rawFocus : null;
        return { correction: rawCorrection, correctionFocus: validFocus };
      })
      .filter(Boolean);
    // Unconditional now (was gated on corrections.length before Step 3.2) — every graded turn
    // gets a star rating, including error-free ones, not just turns with a correction to show.
    // Confirmed safe: turnMeta[idx] is never checked for plain existence anywhere in this file,
    // only via `?.corrections`/`?.stars`/`?.errorTag` on specific fields (see the declaration's
    // comment) — those already tolerate an entry that's present but has a field as null/empty.
    syncTurnMeta(p => ({
      ...p,
      [messagesRef.current.length - 1]: { corrections, stars: result?.stars ?? null, errorTag: result?.errorTag ?? null, grammarPointId: result?.grammarPointId ?? null, feedback: result?.feedback ?? null },
    }));
    if (isFinalTurn) endSession();
    // speakReply resolves, never rejects — a TTS failure lands in ttsNotice, a blocked autoplay
    // lands in a tap-to-play ttsNotice. The text is already on screen either way.
    const audioIdx = messagesRef.current.length - 1;
    const spoken = await speakReply(clean);
    if (s !== sessionRef.current) return;   // speakReply can take seconds — recheck before the reset
    if (spoken) setTurnAudio(p => ({ ...p, [audioIdx]: spoken }));
    setVoiceStage("idle");
  };

  // Guards against a fast double-tap on "Try again" firing two concurrent generations (which
  // would append two assistant messages → consecutive same-role messages → Anthropic 400).
  const retryingRef = useRef(false);

  // Manual "Try again" — brief pause, then re-run ONLY the generation step for the current turn
  // (the learner's turn is already in messagesRef, never re-appended). Bails if the learner
  // navigated away during the pause.
  const retryGeneration = async () => {
    const failure = replyError;
    if (!failure || retryingRef.current) return;
    retryingRef.current = true;
    const s = sessionRef.current;
    setReplyError(null);
    setVoiceStage("replying");   // "Babi is thinking…" banner covers the pause + the retry
    await new Promise(r => setTimeout(r, LAB_RETRY_DELAY_MS));
    if (s === sessionRef.current && isTurnActive()) {
      const next = { attempt: failure.attempt + 1 };
      if (failure.mode === "opening") await generateOpening(next);
      else await generateReply(next);
    }
    retryingRef.current = false;
  };

  // Babi's opening move — she always speaks first about the chosen topic.
  const startConversation = async () => {
    if (!sessionStartedAtRef.current) sessionStartedAtRef.current = Date.now();
    setPhase("talk");
    await generateOpening({ attempt: 0 });
  };

  // One learner turn → Babi's conversational reply.
  const runReply = async (text) => {
    appendUserTurn(text);
    await generateReply({ attempt: 0 });
  };
  useEffect(() => { runReplyRef.current = runReply; });

  // A bare `new Audio()` isn't in the DOM, so it keeps playing on its own even after this
  // component unmounts (same reason useVoiceTurn's own currentAudioRef needs an explicit pause on
  // cancelTurn/unmount) — stop a "Listen again" replay in flight when the learner navigates away.
  useEffect(() => () => { replayAudioRef.current?.pause(); }, []);

  // Grow the transcript-review textarea to fit its content (same approach as the widget's).
  useLayoutEffect(() => {
    const ta = pendingTextareaRef.current;
    if (ta) { ta.style.height = "auto"; ta.style.height = `${ta.scrollHeight}px`; }
  }, [pendingVoiceText]);

  // Bumps sessionRef (the stale-async guard) and clears everything keyed by the OLD session's
  // message indices — per-turn render metadata, translations, explain-more results, and cached
  // "Listen again" audio (stopping any replay actually in flight first) — so a late-resolving
  // call can't paint onto a fresh session, and a stale clip can't keep playing into the new one.
  const resetTurnState = () => {
    sessionRef.current += 1;
    stopReplayAudio();
    setTranslations({});
    syncTurnMeta({});
    setExplainMore({});
    setTurnAudio({});
  };

  const resetSessionCredit = () => { sessionStartedAtRef.current = null; sessionCreditedRef.current = false; };

  const pickTopic = (tp) => {
    armTurn();  // re-arm the hook's cancel guard for this new session (restart() disarmed it)
    resetTurnState();
    resetSessionCredit();
    setTopic(tp);
    setPhase("intro");
    syncMessages([]);
    setReplyFailed(false);
    setReplyError(null);
    setTranscriptState("none");
    setTtsNotice(null);
    setTtsRate(LAB_TTS_RATE_NORMAL);  // speed control resets every new session — never persisted
  };

  const restart = () => {
    // cancelTurn() stops any in-flight capture / STT / reply / playing audio and resets
    // voiceStage / pending / ttsNotice — this path does NOT unmount, so nothing else would.
    cancelTurn();
    resetTurnState();
    resetSessionCredit();
    setPhase("pick");
    setTopic(null);
    syncMessages([]);
    setReplyFailed(false);
    setReplyError(null);
  };

  // Stops a "Listen again" replay in flight, if any — used before starting a different replay,
  // when a fresh live turn is about to speak (so a replay can never overlap the live reply), and
  // on session reset / unmount.
  const stopReplayAudio = () => {
    replayAudioRef.current?.pause();
    replayAudioRef.current = null;
    setReplayingIndex(null);
  };

  // A tap on "Listen again" for one assistant turn — no API call, no billing: just a fresh local
  // `Audio` built from the base64 clip `speakReply` already cached in `turnAudio` at generation
  // time (see generateOpening/generateReply). Only one clip plays at a time: a tap on the
  // currently-playing turn stops it (toggle-off); a tap on a different turn stops whatever was
  // playing first, same as the live reply/translate controls already being hidden while Babi
  // herself is mid-speech avoids this class of overlap for the ORIGINAL playback.
  //
  // Speed correction: the clip was synthesized at `entry.rate` (whatever the toggle said back
  // when that turn was generated), which may no longer match the CURRENT toggle value (`ttsRate`)
  // — the learner may have flipped it since, on this very turn or any earlier one. Rather than
  // re-synthesizing (a new billed API call), the ratio between the two is applied as the browser's
  // own `playbackRate` on top of the already-generated audio: 1.0 whenever they match (the common
  // case — no stretch, full native TTS quality), and a corrective stretch/speed-up otherwise, so
  // a "Listen again" tap always plays back at whatever speed is currently selected.
  const playTurnAudio = (index) => {
    const wasPlayingThis = replayingIndex === index;
    stopReplayAudio();
    if (wasPlayingThis) return; // toggle off
    const entry = turnAudio[index];
    if (!entry) return;
    const audio = new Audio(`data:audio/mp3;base64,${entry.audioContent}`);
    audio.playbackRate = ttsRate / (entry.rate || LAB_TTS_RATE_NORMAL);
    replayAudioRef.current = audio;
    setReplayingIndex(index);
    const stop = () => {
      if (replayAudioRef.current === audio) { replayAudioRef.current = null; setReplayingIndex(null); }
    };
    audio.addEventListener("ended", stop, { once: true });
    audio.addEventListener("error", stop, { once: true });
    audio.play().catch(stop); // e.g. blocked by the same autoplay policy as a fresh reply would be — this is always a direct tap, so in practice it shouldn't be blocked, but never leave the button stuck in a "playing" state if it somehow is
  };

  // "Listen again" for one graded Babi bubble — only shown when a clip actually got cached for
  // it (opening included; `turnAudio` isn't gated to graded turns the way turnMeta/corrections
  // are). Nothing renders when TTS was off, failed, or unavailable for that turn.
  const renderListenAgain = (index) => {
    if (!turnAudio[index]) return null;
    const isPlaying = replayingIndex === index;
    return (
      <button type="button" className={`lab-listen-again-btn${isPlaying ? " lab-listen-again-btn--playing" : ""}`} onClick={() => playTurnAudio(index)}>
        {isPlaying ? t("lab.listenAgainPlaying") : t("lab.listenAgainButton")}
      </button>
    );
  };

  // A tap on "Translate what Babi said" for one assistant turn — one on-demand Sonnet call, no
  // caching (Lab text is unique per session). Dropped if the session changed while in flight.
  // Only one translation runs at a time, matching Slice 1's single-flight behaviour.
  const translateNow = async (index, sourceText) => {
    if (!sourceText) return;
    if (Object.values(translations).some(v => v?.status === "loading")) return;
    const s = sessionRef.current;
    setTranslations(p => ({ ...p, [index]: { status: "loading" } }));
    let out = "";
    try { out = (await translateBabiUtterance(sourceText, baseLanguage) || "").trim(); } catch (_) { out = ""; }
    if (s !== sessionRef.current) return;   // session changed under us — discard
    setTranslations(p => ({ ...p, [index]: out ? { status: "done", text: out } : { status: "error" } }));
  };

  // The translate control shown under one Babi bubble, split in two so the BUTTON can sit in a
  // side-by-side row next to "Listen again" (renderTurnActions below) while the expanded result —
  // full-width text plus a ✕ to dismiss — still renders on its own line underneath, same as
  // before the split. Base-language text; RTL-isolated via renderChatContent.
  const renderTranslateButton = (index, sourceText) => {
    if (!canTranslate) return null;
    const tr = translations[index];
    if (tr?.status === "done") return null; // shown by renderTranslateResult instead
    return (
      <button type="button" className="lab-translate-btn" onClick={() => translateNow(index, sourceText)} disabled={tr?.status === "loading"}>
        {tr?.status === "loading" ? t("lab.translating") : t("lab.translateButton")}
      </button>
    );
  };
  const renderTranslateResult = (index) => {
    if (!canTranslate) return null;
    const tr = translations[index];
    if (tr?.status === "error") return <p className="lab-hint" dir="auto">{t("lab.translateFailed")}</p>;
    if (tr?.status !== "done") return null;
    return (
      <div className="lab-translation" dir="auto">
        <span>{renderChatContent(tr.text, profile?.name)}</span>
        <button type="button" className="lab-translation-hide"
          onClick={() => setTranslations(p => { const n = { ...p }; delete n[index]; return n; })}
          aria-label={t("chat.voiceBannerDismiss")} title={t("chat.voiceBannerDismiss")}>✕</button>
      </div>
    );
  };

  // "Listen again" + the translate button, side by side — DOM order [Listen again, Translate],
  // with `dir="auto"` doing the LTR/RTL mirroring: flex-direction stays plain "row", so the first
  // child sits at the inline-start edge (left in LTR, right in RTL) exactly as asked — Listen
  // again left/Translate right for LTR languages, reversed for RTL, with no isRTL branching needed.
  const renderTurnActions = (index, sourceText) => (
    <div className="lab-turn-actions" dir="auto">
      {renderListenAgain(index)}
      {renderTranslateButton(index, sourceText)}
    </div>
  );

  // Step 3.2 (§6 point 5): the 1-3 star motivator for one graded turn. null (not 0 — askLabReply
  // always resolves to an integer 1-3 once a turn is graded) means this turn was never graded —
  // Babi's opening, or a turn whose generation failed before grading ran — so nothing renders.
  const renderStars = (index) => {
    const stars = turnMeta[index]?.stars;
    if (stars == null) return null;
    return (
      <div className="lab-stars" role="img" aria-label={t("lab.starsAriaLabel", { stars })}>
        {[1, 2, 3].map(n => (
          <span key={n} className={`lab-star${n <= stars ? " lab-star--filled" : ""}`} aria-hidden="true">
            {n <= stars ? "★" : "☆"}
          </span>
        ))}
      </div>
    );
  };

  // "Explain more" (§6 point 6) — the on-demand third call, only ever fired by this explicit tap
  // (never automatic, never part of the combined grading+reply call). Mirrors translateNow above
  // exactly: same on-demand-secondary-lookup shape, same simple try/catch → status "error" with
  // no special retry handling — isRetryableReplyFailure/"Try again" exist for the PRIMARY
  // reply-generation flow (turn cap, session-ending, LAB_REPLY_RETRY_CAP), none of which apply to
  // a supplementary explanation lookup; the button staying tappable again IS the retry, same as
  // "Translate what Babi said" already works today.
  const explainMoreNow = async (index) => {
    if (Object.values(explainMore).some(v => v?.status === "loading")) return;
    const meta = turnMeta[index];
    const learnerUtterance = messagesRef.current[index - 1]?.content || "";
    const correction = (meta?.corrections || []).map(c => c.correction).join("; ");
    const s = sessionRef.current;
    setExplainMore(p => ({ ...p, [index]: { status: "loading" } }));
    let out = "";
    try {
      out = (await askLabExplainMore(targetLanguage, baseLanguage, learnerUtterance, correction, meta?.errorTag, meta?.feedback) || "").trim();
    } catch (_) { out = ""; }
    if (s !== sessionRef.current) return;   // session changed under us — discard
    setExplainMore(p => ({ ...p, [index]: out ? { status: "done", text: out } : { status: "error" } }));
  };

  // The "Explain more" control shown under one graded Babi bubble — only offered when that turn
  // actually had a mistake (errorTag non-null; a stars:3 turn has nothing to explain). Same
  // button → loading → text (with ✕ to dismiss) shape as renderTranslate above.
  const renderExplainMore = (index) => {
    const meta = turnMeta[index];
    if (!meta?.errorTag) return null;
    const ex = explainMore[index];
    if (ex?.status === "done") return (
      <div className="lab-explain" dir="auto">
        <span>{renderChatContent(ex.text, profile?.name)}</span>
        <button type="button" className="lab-translation-hide"
          onClick={() => setExplainMore(p => { const n = { ...p }; delete n[index]; return n; })}
          aria-label={t("chat.voiceBannerDismiss")} title={t("chat.voiceBannerDismiss")}>✕</button>
      </div>
    );
    return (
      <>
        <button type="button" className="lab-explain-btn" onClick={() => explainMoreNow(index)} disabled={ex?.status === "loading"}>
          {ex?.status === "loading" ? t("lab.explaining") : t("lab.explainButton")}
        </button>
        {ex?.status === "error" && <p className="lab-hint" dir="auto">{t("lab.explainFailed")}</p>}
      </>
    );
  };

  // Single-session recap card (Step 3.2 follow-up) — see computeSessionRecap's own doc comment
  // for what's derived and why. §7's cross-session tally is separate, later work; nothing here
  // depends on it. No card at all when there's nothing graded to recap.
  const renderRecap = () => {
    const recap = computeSessionRecap(turnMeta, messages);
    if (!recap) return null;
    const { gradedCount, avgStars, errorFreeCount, mistakesByType, correctionRecap } = recap;
    const roundedAvg = Math.round(avgStars);

    return (
      <div className="lab-recap" dir="auto">
        <p className="lab-recap-heading">{t("lab.recapHeading")}</p>
        <p className="lab-recap-title">{topicName || t("lab.recapTitle")}</p>
        <div className="lab-recap-headline">
          <div>
            <span className="lab-recap-avg-label">{t("lab.recapAvgStars")}</span>
            <div className="lab-stars" role="img" aria-label={t("lab.starsAriaLabel", { stars: roundedAvg })}>
              {[1, 2, 3].map(n => (
                <span key={n} className={`lab-star${n <= roundedAvg ? " lab-star--filled" : ""}`} aria-hidden="true">
                  {n <= roundedAvg ? "★" : "☆"}
                </span>
              ))}
            </div>
          </div>
          <p className="lab-recap-turns">{t("lab.recapTurnsCompleted", { completed: gradedCount, total: LAB_MAX_LEARNER_TURNS })}</p>
        </div>
        <p className="lab-recap-sub">{t("lab.recapErrorFree", { n: errorFreeCount })}</p>
        {Object.keys(mistakesByType).length > 0 && (
          <div className="lab-recap-section">
            <p className="lab-recap-section-title">{t("lab.recapMistakesByType")}</p>
            <div className="lab-recap-chips">
              {Object.entries(mistakesByType).map(([tag, count]) => (
                <span key={tag} className="lab-recap-chip">{tag} · {count}</span>
              ))}
            </div>
          </div>
        )}
        {correctionRecap.length > 0 && (
          <div className="lab-recap-section">
            <p className="lab-recap-section-title">{t("lab.recapCorrections")}</p>
            <div className="lab-recap-corrections">
              {correctionRecap.map(({ recapKey, learnerLine, correction, correctionFocus }) => (
                <div className="lab-recap-correction-item" key={recapKey}>
                  <span className="lab-recap-correction-said">{renderChatContent(learnerLine, profile?.name)}</span>
                  <span className="lab-recap-correction-arrow" aria-hidden="true">→</span>
                  {renderCorrectionRun(correction, correctionFocus, `${recapKey}-fix`)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // The corrected phrase, rendered bold green (calm "here's the right version") — with the
  // word(s) that actually changed (correctionFocus) picked out in bold red inside it, so the
  // learner reads the red as "here is the mistake". Falls back to all-green when there's no
  // usable focus (model omitted it, or it isn't a clean single-occurrence substring).
  const renderCorrectionRun = (correction, focus, key) => {
    // Only split out the focus when it occurs exactly once in the phrase — otherwise
    // (0 hits, or the focus substring also sits inside another word) fall back to all-green.
    const segs = focus ? correction.split(focus) : [correction];
    return (
      <strong className="lab-correction" key={key}>
        {segs.length === 2
          ? segs.flatMap((seg, i) => {
              const parts = i === 0 ? [] : [<span className="lab-correction-focus" key={`f${i}`}>{focus}</span>];
              if (seg) parts.push(<Fragment key={`p${i}`}>{seg}</Fragment>);
              return parts;
            })
          : correction}
      </strong>
    );
  };

  // Babi's bubble, with each corrected phrase (up to LAB_MAX_CORRECTIONS_PER_REPLY, in the order
  // Babi said them) pulled out and highlighted in turn. Every other slice of the text still goes
  // through renderChatContent for the same bidi isolation as an un-corrected reply. `remaining`
  // only ever shrinks from the front, so corrections are matched left-to-right against what's
  // left — a near-miss on one entry just skips that one highlight (falls through as plain text
  // later), it doesn't corrupt or drop the rest of the bubble.
  const renderBabiBubble = (content, corrections) => {
    if (!corrections?.length) return renderChatContent(content, profile?.name);
    const nodes = [];
    let remaining = content;
    corrections.forEach(({ correction, correctionFocus }, i) => {
      const idx = correction ? remaining.indexOf(correction) : -1;
      if (idx === -1) return;
      const before = remaining.slice(0, idx);
      if (before) nodes.push(<Fragment key={`s${i}`}>{renderChatContent(before, profile?.name)}</Fragment>);
      nodes.push(renderCorrectionRun(correction, correctionFocus, `c${i}`));
      remaining = remaining.slice(idx + correction.length);
    });
    if (remaining) nodes.push(<Fragment key="s-last">{renderChatContent(remaining, profile?.name)}</Fragment>);
    return nodes;
  };

  // Every real conversation turn (messages[0] is the hidden LAB_KICKOFF). Fragments, not wrapper
  // divs, so .lab-translation's `align-self` still resolves against the flex column it sits in
  // (.lab-stage in "talk", .lab-exchange in "ended"). Listen-again/translate/explain-more controls
  // are hidden while Babi is mid-generation or mid-speech, same as Slice 1 — stars are a settled
  // result for a past turn, not an active control, so they stay visible regardless of voiceStage.
  // (Listen-again specifically: this also guarantees a cached replay can never play alongside
  // Babi's own live speech — generateReply additionally calls stopReplayAudio() itself so a
  // replay already in flight when a new turn starts gets stopped, not just hidden.)
  const showTranslateControls = voiceStage !== "replying" && voiceStage !== "speaking";
  const renderTranscript = () => messages.slice(1).map((m, i) => {
    const idx = i + 1;
    const isAssistant = m.role === "assistant";
    return (
      <Fragment key={idx}>
        <div className={`chat-bubble ${isAssistant ? "assistant" : "user"}`} dir="auto">
          {isAssistant ? renderBabiBubble(m.content, turnMeta[idx]?.corrections) : renderChatContent(m.content, profile?.name)}
        </div>
        {isAssistant && renderStars(idx)}
        {isAssistant && showTranslateControls && renderTurnActions(idx, m.content)}
        {isAssistant && showTranslateControls && renderTranslateResult(idx)}
        {isAssistant && showTranslateControls && renderExplainMore(idx)}
      </Fragment>
    );
  });

  // Header "Back": on the topic list it exits to the dashboard; anywhere inside a session it
  // returns to the topic list (never the session-complete screen).
  const handleHeaderBack = () => { if (phase === "pick") onBack(); else restart(); };

  const topicName = topic ? getTranslatedTopicName(topic, lang) : "";

  return (
    <div className="goals-view">
      <div className="lab-header">
        <button className="back-btn lab-header-back" onClick={handleHeaderBack}>{t("lab.back")}</button>
        <div className="lab-header-title">{t("lab.title")}</div>
        <img className="lab-header-avatar" src="/babi%20fantasy2.jpg?v=2" alt="Babi" />
      </div>

      {phase === "pick" && (
        <div className="lab-minutes-card" dir="auto">
          <span className="lab-minutes-head"><TimerIcon />{t("lab.voiceMinutesTitle")}</span>
          <span className={`lab-minutes-value${voiceMonthlyRemaining <= 0 && voicePurchasedSeconds <= 0 ? " lab-minutes-value--empty" : ""}`}>
            {voiceMonthlyRemaining <= 0
              ? t("lab.voiceMinutesNone")
              : t("lab.voiceMinutesLeft", { amount: formatVoiceDuration(voiceMonthlyRemaining), total: formatVoiceMinutes(voiceLimitSeconds) })}
          </span>
          {voicePurchasedSeconds > 0 && (
            <span className="lab-minutes-purchased">
              {t("settings.voiceMinutesPurchasedExtra", { extra: formatVoiceMinutes(voicePurchasedSeconds) })}
            </span>
          )}
        </div>
      )}

      {phase === "pick" && (
        <div className="goals-card">
          <div className="goals-section-title">{t("lab.pickTopic")}</div>
          <p className="lab-intro">{t("lab.intro")}</p>
          {completedList.length === 0 ? (
            <p className="lab-empty">{t("lab.noTopics")}</p>
          ) : (
            <div className="lab-topic-list" dir={isRTL ? "rtl" : "ltr"}>
              {completedList.map(tp => (
                <button key={tp.topic_id} className="lab-topic-btn" onClick={() => pickTopic(tp)}>
                  <bdi className="lab-topic-name">{getTranslatedTopicName(tp, lang)}</bdi>
                  <span className="lab-topic-level">{t("lab.level", { level: tp.cefr_level })}</span>
                  <span className="lab-topic-arrow">{isRTL ? "←" : "→"}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {phase === "intro" && (
        <div className="goals-card lab-stage">
          <div className="goals-section-title" dir="auto">{t("lab.talkAbout", { topic: topicName })}</div>
          <p className="lab-intro">{t("lab.intro")}</p>
          <button className="lab-start-btn" onClick={startConversation}>{t("lab.startConversation")}</button>
        </div>
      )}

      {phase === "talk" && (
        <div className="goals-card lab-stage">
          <div className="goals-section-title" dir="auto">{t("lab.talkAbout", { topic: topicName })}</div>

          {/* Speed control — always visible during talk, changeable mid-conversation. The next
              LIVE reply is synthesized at this value; "Listen again" on any turn (this one or an
              earlier one) always re-derives its playback speed from this CURRENT value instead —
              see ttsRate's declaration comment and playTurnAudio. Never interrupts audio already
              playing. The voice-minutes cost hint only shows once Slow is actually selected — no
              need to caveat the default, unselected state. */}
          <div className="lab-speed-row" dir="auto">
            <span className="lab-speed-label">{t("lab.speedLabel")}</span>
            <div className="lab-speed-toggle" role="group" aria-label={t("lab.speedLabel")}>
              <button
                type="button"
                className={`lab-speed-btn${ttsRate === LAB_TTS_RATE_NORMAL ? " lab-speed-btn--active" : ""}`}
                onClick={() => setTtsRate(LAB_TTS_RATE_NORMAL)}
              >
                {t("lab.speedNormal")}
              </button>
              <button
                type="button"
                className={`lab-speed-btn${ttsRate === LAB_TTS_RATE_SLOW ? " lab-speed-btn--active" : ""}`}
                onClick={() => setTtsRate(LAB_TTS_RATE_SLOW)}
              >
                {t("lab.speedSlow")}
              </button>
            </div>
          </div>
          {ttsRate === LAB_TTS_RATE_SLOW && <p className="lab-hint" dir="auto">{t("lab.speedNote")}</p>}

          {renderTranscript()}

          {voiceStage === "consent" ? (
            <div className="voice-consent-card" dir="auto">
              <p className="voice-consent-title">{t("chat.voiceConsentTitle")}</p>
              <p className="voice-consent-body">{t("chat.voiceConsentBody")}</p>
              <a className="voice-consent-privacy-link" href="https://speafy.com/privacy" target="_blank" rel="noopener noreferrer">{t("chat.voiceConsentPrivacyLink")}</a>
              <div className="voice-consent-actions">
                <button className="voice-consent-allow" onClick={() => requestMic(true)}>{t("chat.voiceConsentAllow")}</button>
                <button className="voice-consent-decline" onClick={() => setVoiceStage("idle")}>{t("chat.voiceConsentDecline")}</button>
              </div>
            </div>
          ) : (
            <>
              {voiceStage === "idle" && pendingVoiceText === null && transcriptState !== "empty"
                && learnerCanRespond && learnerTurnCount === 0 && (
                <p className="lab-hint" dir="auto">{t("lab.tapToStart")}</p>
              )}

              {pendingVoiceText !== null && (
                <div
                  className="chat-bubble user voice-pending-bubble"
                  dir="auto"
                  onClick={e => { if (!e.target.closest("button")) pendingTextareaRef.current?.focus(); }}
                >
                  <textarea
                    ref={pendingTextareaRef}
                    rows={1}
                    className="voice-pending-input"
                    // Resolve direction from the transcript's OWN text, not the bubble's
                    // dir="auto" (which the Farsi review hint below drags to RTL) — a Turkish
                    // transcript reads left-to-right, a Farsi edit right-to-left.
                    dir="auto"
                    value={pendingVoiceText}
                    onFocus={beginEditingPendingVoiceText}
                    onChange={e => { beginEditingPendingVoiceText(); setPendingVoiceText(e.target.value); }}
                    aria-label={t("chat.voiceEditTranscript")}
                  />
                  {/* No auto-send countdown — the Lab passes autoSendPendingReview:false, so this
                      review stays until the learner acts: Confirm (✓), edit-then-Confirm, or
                      Redo (↻ → discard + re-open the mic). */}
                  <div className="voice-pending-actions">
                    <span className="voice-pending-hint">{t("lab.voiceReviewHint")}</span>
                    <button type="button" className="voice-pending-send" onClick={() => confirmPendingVoiceText(pendingVoiceText)} aria-label={t("lab.voiceConfirm")} title={t("lab.voiceConfirm")}>✓</button>
                    <button type="button" className="voice-pending-discard" onClick={redoPendingVoiceText} aria-label={t("lab.voiceRedo")} title={t("lab.voiceRedo")}>↻</button>
                  </div>
                </div>
              )}

              {/* Persistent low-balance warning — independent of voiceStage (stays up across
                  idle/capturing/replying/speaking, unlike the turn-status banners below), so it
                  doesn't disappear and reappear as the learner moves through a turn while still
                  low. No dismiss button, deliberately: this must reappear/stay for as long as the
                  condition holds, not act like a one-time toast the learner can permanently hide
                  while still under the threshold. shouldShowLowBalanceWarning already excludes
                  voiceStage === "no-balance" (and remaining <= 0 outright) so this never doubles
                  up with the hard-stop banner just below. */}
              {shouldShowLowBalanceWarning(voiceRemainingSeconds, voiceStage) && (
                <div className="voice-banner voice-banner-low-balance" dir="auto">
                  <span>{t("lab.lowBalanceWarning")}</span>
                </div>
              )}

              {(voiceStage === "denied" || voiceStage === "device-in-use" || voiceStage === "error"
                || voiceStage === "not-entitled" || voiceStage === "no-balance" || voiceStage === "other-tab-active") && (
                <div className={`voice-banner voice-banner-${voiceStage}`} dir="auto">
                  <span>{t(
                    // lab.voiceDenied, not the widget's shared chat.voiceDenied — that string
                    // offers "keep chatting by typing", which isn't true here: the Lab has no
                    // text-input fallback at all. Every other banner below is still accurate for
                    // both surfaces, so only this one diverges.
                    voiceStage === "denied" ? "lab.voiceDenied"
                      : voiceStage === "device-in-use" ? "chat.voiceDeviceInUse"
                      : voiceStage === "not-entitled" ? "chat.voiceNotEntitled"
                      : voiceStage === "no-balance" ? "chat.voiceNoBalance"
                      : voiceStage === "other-tab-active" ? "chat.voiceOtherTabActive"
                      : "chat.voiceError"
                  )}</span>
                  <button className="voice-banner-close" onClick={() => setVoiceStage("idle")} aria-label={t("chat.voiceBannerDismiss")}>✕</button>
                </div>
              )}

              {(voiceStage === "starting" || voiceStage === "capturing" || voiceStage === "processing"
                || voiceStage === "replying" || voiceStage === "speaking") && (
                <div
                  className={`voice-banner voice-banner-active${
                    voiceStage === "starting" ? " voice-banner--starting"
                      : voiceStage === "capturing" ? " voice-banner--listening"
                      : ""
                  }`}
                  dir="auto"
                >
                  <span>{t(
                    voiceStage === "starting" ? "chat.voiceStarting"
                      : voiceStage === "capturing" ? "chat.voiceListening"
                      : voiceStage === "processing" ? "chat.voiceProcessing"
                      : voiceStage === "replying" ? "lab.thinking"
                      : "chat.voiceSpeaking"
                  )}</span>
                </div>
              )}

              {voiceStage === "idle" && transcriptState === "empty" && (
                <div className="voice-banner voice-banner-empty" dir="auto">
                  <span>{t("chat.voiceDidntCatch")}</span>
                  <button className="voice-banner-close" onClick={() => setTranscriptState("none")} aria-label={t("chat.voiceBannerDismiss")}>✕</button>
                </div>
              )}

              {/* Retryable reply/opening failure — the learner's turn is preserved, they can
                  retry (up to LAB_REPLY_RETRY_CAP) or leave via the header Back. A non-retryable
                  or cap-reached failure never gets here — it ends the session directly. */}
              {replyError && voiceStage === "idle" && (
                <>
                  <div className="voice-banner voice-banner-error" dir="auto"><span>{t("lab.replyError")}</span></div>
                  <button type="button" className="lab-retry-btn" onClick={retryGeneration}>{t("lab.tryAgain")}</button>
                </>
              )}

              {micSupported ? (
                voiceStage !== "reviewing" && learnerCanRespond && (
                  // learnerCanRespond already guarantees the last message was Babi's (see its
                  // definition above) — including the very first turn, right after Babi's
                  // opening message. So every turn, turn 0 included, is "answer the question
                  // Babi just asked": the full-width gold CTA (.lab-continue-btn), distinct only
                  // from the ⏹ stop state while actively capturing.
                  (() => {
                    const isContinueCta = voiceStage !== "capturing";
                    return (
                      <button
                        type="button"
                        className={isContinueCta
                          ? "lab-continue-btn"
                          : `lab-mic-btn${voiceStage === "capturing" ? " lab-mic-btn--active" : ""}`}
                        onClick={handleMicClick}
                        disabled={["starting", "processing", "replying", "speaking"].includes(voiceStage)}
                        aria-label={t(voiceStage === "capturing" ? "chat.voiceStopBtn" : "lab.continueConversation")}
                      >
                        {voiceStage === "capturing"
                          ? `⏹ ${t("chat.voiceStopBtn")}`
                          : <>🎤 {t("lab.continueConversation")} <span className="lab-continue-btn-arrow" aria-hidden="true">{isRTL ? "←" : "→"}</span></>}
                      </button>
                    );
                  })()
                )
              ) : (
                <p className="lab-empty">{t("chat.voiceError")}</p>
              )}

              {/* Deliberate early end — only offered once at least one full exchange has
                  happened (so it always earns streak credit) and while it's the learner's
                  idle turn. endSession() runs the same credit path as reaching the cap. */}
              {voiceStage === "idle" && pendingVoiceText === null && learnerCanRespond && learnerTurnCount >= 1 && (
                <button type="button" className="lab-finish-btn" onClick={endSession}>
                  {t("lab.finishSession")}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {phase === "ended" && (
        <div className="goals-card">
          <div className="lab-exchange">
            {renderTranscript()}
            {replyFailed && (
              <div className="voice-banner voice-banner-error" dir="auto"><span>{t("lab.replyError")}</span></div>
            )}
          </div>

          {voiceStage === "speaking" && <p className="lab-hint" dir="auto">🔊 {t("chat.voiceSpeaking")}</p>}
          {ttsNotice?.type === "tap-to-play" && (
            <button
              className="voice-tap-to-play-btn"
              onClick={() => { ttsNotice.audio?.play().catch(() => {}); setTtsNotice(null); }}
            >🔊 {t("chat.voiceTapToHear")}</button>
          )}
          {/* The "no voice for {language}" case never applies here — the Lab always
              synthesizes in the target language (Turkish), which has voices. That leaves the
              generic TTS-failure notice, plus the rare server-side voice-gate refusal on the
              TTS call (out of minutes / not entitled), shown with the same copy as the
              pre-call mic-button block above. */}
          {ttsNotice && ttsNotice.type !== "tap-to-play" && (
            <p className="lab-hint" dir="auto">{t(
              ttsNotice.type === "not-entitled" ? "chat.voiceNotEntitled"
                : ttsNotice.type === "no-balance" ? "chat.voiceNoBalance"
                : "chat.voiceTtsUnavailable"
            )}</p>
          )}

          {/* Below the transcript, not above it — the learner reads the conversation top to
              bottom first, so the recap lands right where their eye naturally ends up instead of
              being scrolled past before they've even seen what it's summarizing. */}
          {renderRecap()}

          <div className="lab-complete">
            <p className="lab-complete-title">{t("lab.complete")}</p>
            <p className="lab-complete-sub">{t("lab.completeSub")}</p>
            <div className="lab-complete-actions">
              <button className="lab-mic-btn lab-another-btn" onClick={restart}>{t("lab.another")}</button>
              <button className="back-btn" onClick={onBack}>{t("lab.done")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
